import throttle from 'lodash/throttle'
import { v4 as uuid } from 'uuid'
import { getCenter, getDistance } from 'geolib'
import booleanEqual from '@turf/boolean-equal'
import distance from '@turf/distance'
import pubsub from '@/lib/pubsub'
import DrawModeBase from '@/lib/mapbox/draw-mode-base'
import {
  getIndexOfNewCoordinate,
  getNearestPointOnLine,
  getToleranceMeters,
  findVertexNearPoint,
} from '@/lib/mapbox/draw-mode-helpers'
import { getBestFitUnitOfLength } from '@/lib/math'
import { VERTEX_LABEL_LENGTH_CUTOFF } from '@/lib/mapbox/line-mode-constants'
import isEqual from 'lodash/isEqual'
import {
  visibleRegion,
  setCursor,
  resetCursor,
  setFeaturesHighlighted,
  clearFeaturesHighlighted,
} from '@/services/map-service'
import { getItem } from '@/plugins/storage'
import FreehandDrawLineMode from '@/lib/mapbox/freehand-draw-line-mode'
import { MARKUP_COORDINATES_CHANGED } from '@/lib/mapbox/drawing-events'

/**
 * Throttle interval for noisy events (e.g., onMouseMove)
 */
const THROTTLE_INTERVAL = 60

/**
 * Delay used to differentiate between a click and a drag. If mouse is held down longer
 * than this interval (milliseconds), it is considered a drag; otherwise, it is a click.
 */
const DRAG_DELAY = 300

/**
 * Distance threshold used for short-circuiting the drag delay timer (Manhattan distance,
 * in pixels). This is checked on mouse move and mouse up. If current mouse location is
 * farther than this distance from the original mouse down location, then we short circuit
 * the drag delay timer and consider it a drag instead of a click, even if drag delay
 * timer hasn't expired yet.
 */
const DRAG_DIST_THRESHOLD = 8

/**
 * Create/Edit modes for lines and tracks.
 */
class LineEditMode extends DrawModeBase {
  /**
   * @inheritDoc
   */
  onSetup(lineGeoJson, opts = {}) {
    const lineId = lineGeoJson.properties.id
    const vertexIds = this._generateVertexIds(lineGeoJson)
    const labels = this._generateLabels(lineGeoJson)
    const { canUndo, isCreate } = opts

    this._state = {
      lineId,
      vertexIds,
      labels,
      draggingVertexIndex: null,
      dragMoveLocation: null,
      dragMovePoint: null,
      lineHoverLocation: null,
      vertexAddLocation: null,
      vertexAddIndex: null,
      vertexAddId: null,
      isCreate,
      appendVertex: false,
      handleUndo: canUndo ? this._handleUndo.bind(this) : undefined,
      undoQueue: opts.undoQueue || [],
      dragTimer: null,
    }

    if (canUndo) {
      pubsub.subscribe('markupUndo', this._state.handleUndo)
    }

    this._mouseMove = throttle((event) => {
      this._handleMouseMove(event)
    }, THROTTLE_INTERVAL)
  }

  /**
   * @inheritdoc
   */
  onTearDown() {
    resetCursor()
    if (this._state.handleUndo) {
      pubsub.unsubscribe('markupUndo', this._state.handleUndo)
    }
  }

  /**
   * @inheritDoc
   */
  onMouseDown(event) {
    const lineFeature = this._getFeature()
    const isAltPressed = event.originalEvent.altKey

    this._state.visibleRegionMouseDown = visibleRegion()
    this._state.mouseDownLocation = event.point

    // determine if we clicked on a vertex of the line
    const vertexIndex = findVertexNearPoint(
      lineFeature,
      [event.lngLat.lng, event.lngLat.lat],
      { threshold: this._getVertexClickToleranceForZoom() }
    )?.index
    if (isAltPressed && vertexIndex >= 0) {
      this._removeVertex(vertexIndex, true)
      return
    } else if (vertexIndex >= 0) {
      const coordinate = [event.lngLat.lng, event.lngLat.lat]
      this._startDragTimer('drag_vertex', {
        coordinate,
        vertexIndex,
      })
      return
    }

    if (this._isHoveringLine()) {
      this._startDragTimer('split_segment', event)
    }
  }

  /**
   * @inheritDoc
   */
  onMouseUp(event) {
    const isAltPressed = event.originalEvent.altKey

    this._clearDragTimer()
    this.map.dragPan.enable()
    this._checkPendingDragOperation(event.point)
    if (this._isDraggingVertex()) {
      this._endDraggingVertex()
    } else if (this._state.isCreate && !isAltPressed) {
      const region = visibleRegion()
      const panning = !booleanEqual(region, this._state.visibleRegionMouseDown)
      if (!panning) {
        this._state.appendVertex = true
        this._handleAddVertex(event)
      }
    }
  }

  /**
   * @inheritDoc
   */
  onMouseMove(event) {
    // Call throttled function _mouseMove to prevent performing too many expensive
    // operations on every event. If sufficient delay has elapsed, _handleMouseMove
    // will be called.
    this._mouseMove(event)
  }

  /**
   * Handles the mouse move event
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleMouseMove(event) {
    this._checkPendingDragOperation(event.point)
    if (this._isDraggingVertex()) {
      this._handleVertexDrag(event)
      return
    }

    const features = this.getFeaturesAtPoint(event.point)
    const lineId = this._state.lineId
    const isHoveringLine = features.some((feature) => feature.id === lineId)
    const hoveredVertices = features.filter(
      (feature) => feature.properties.meta === 'vertex'
    )
    const isHoveringVertex = hoveredVertices.length > 0

    if (isHoveringVertex) {
      setCursor('grab')
      setFeaturesHighlighted([hoveredVertices[0]])
    } else {
      resetCursor()
      clearFeaturesHighlighted()
    }

    if (isHoveringLine && !isHoveringVertex) {
      this._handleHoverPoint(event)
    } else {
      this._removeHoverPoint()
    }
  }

  /**
   * @inheritDoc
   */
  toDisplayFeatures(geoJson) {
    const line = this._formatLine(geoJson)

    const features = [line]
    features.push(...this._formatVertices(line))

    if (
      !geoJson.properties.free_hand &&
      line.geometry.coordinates.length <= VERTEX_LABEL_LENGTH_CUTOFF
    ) {
      features.push(...this._state.labels)
    }

    if (this._isHoveringLine()) {
      const hoverPoint = this._formatHoverPoint()
      features.push(hoverPoint)
    }

    return features
  }

  /**
   * Gets the vertex click tolerance in meters based on the current map zoom level.
   * Higher zoom levels (more zoomed in) require smaller tolerance values for more precise interaction.
   *
   * @returns {number} The click tolerance in meters:
   *   - 300 for zoom levels <= 10 (zoomed out)
   *   - 100 for zoom levels 11-13 (medium zoom)
   *   - 30 for zoom levels 13-16 (slightly zoomed in)
   *   - 10 for zoom levels 16-19 (zoomed in)
   *   - 4 for zoom levels > 19 (zoooooooomed in)
   * @private
   */
  _getVertexClickToleranceForZoom() {
    const ZOOM_THRESHOLD_LOW = 8
    const ZOOM_THRESHOLD_MEDIUM_LOW = 10
    const ZOOM_THRESHOLD_MEDIUM = 13
    const ZOOM_THRESHOLD_MEDIUM_HIGH = 16
    const ZOOM_THRESHOLD_HIGH = 19

    const TOLERANCE_LOW_ZOOM = 1200
    const TOLERANCE_MEDIUM_LOW_ZOOM = 500
    const TOLERANCE_MEDIUM_ZOOM = 100
    const TOLERANCE_HIGH_ZOOM = 30
    const TOLERANCE_HIGHER_ZOOM = 10
    const TOLERANCE_ULTRA_HIGH_ZOOM = 4

    const currentZoom = this.mapViewer.getZoom()

    if (currentZoom <= ZOOM_THRESHOLD_LOW) {
      return TOLERANCE_LOW_ZOOM
    } else if (
      currentZoom > ZOOM_THRESHOLD_LOW &&
      currentZoom <= ZOOM_THRESHOLD_MEDIUM_LOW
    ) {
      return TOLERANCE_MEDIUM_LOW_ZOOM
    } else if (
      currentZoom > ZOOM_THRESHOLD_MEDIUM_LOW &&
      currentZoom <= ZOOM_THRESHOLD_MEDIUM
    ) {
      return TOLERANCE_MEDIUM_ZOOM
    } else if (
      currentZoom > ZOOM_THRESHOLD_MEDIUM &&
      currentZoom <= ZOOM_THRESHOLD_MEDIUM_HIGH
    ) {
      return TOLERANCE_HIGH_ZOOM
    } else if (
      currentZoom > ZOOM_THRESHOLD_MEDIUM_HIGH &&
      currentZoom <= ZOOM_THRESHOLD_HIGH
    ) {
      return TOLERANCE_HIGHER_ZOOM
    }

    return TOLERANCE_ULTRA_HIGH_ZOOM
  }

  /**
   * Generates vertex IDs.
   * @param {Object} lineGeoJson - GeoJSON feature.
   * @return {Array<string>} Array of generated vertex IDs.
   * @private
   */
  _generateVertexIds(lineGeoJson) {
    return lineGeoJson.geometry.coordinates.map(() => uuid())
  }

  /**
   * Generate the distance labels for the line
   * @param {Object} lineGeoJson - GeoJSON feature.
   * @return {Array<Object>} Array of label geoJson.
   * @private
   */
  _generateLabels(lineGeoJson) {
    const coordinatesList = lineGeoJson.geometry.coordinates
    const labels = []

    // Using for loop here since we're grabbing multiple items in the array
    // during each iteration
    for (let i = 0; i < coordinatesList.length - 1; i++) {
      const coordinates = [coordinatesList[i], coordinatesList[i + 1]]
      const label = this._generateLabel(coordinates)

      labels.push(label)
    }

    return labels
  }

  /**
   * Generate the GeoJson for a distance label between two points
   * @param {Array<Array>} coordinates - The two coordinates making up
   * the segment of the line the label is for
   * @returns {Object} The GeoJson object for the label
   */
  _generateLabel(coordinates) {
    const unitType = getItem('unitType')
    const distance = getDistance(...coordinates)
    const center = getCenter(coordinates)
    const id = uuid()
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [center.longitude, center.latitude],
      },
      properties: {
        id,
        title: getBestFitUnitOfLength({
          length: distance,
          from: 'meters',
          unitType: unitType,
        }),
        meta: 'label',
      },
    }
  }

  /**
   * Starts dragging a vertex.
   * @param {Array<number>} coordinate - Longitude, latitude coordinate.
   * @param {string} vertexId - ID of vertex being dragged.
   * @private
   */
  _startDragVertex(coordinate, vertexIndex) {
    this.map.dragPan.disable()
    this._state.draggingVertexIndex = vertexIndex
    this._state.dragMoveLocation = coordinate
    this._state.startDragLocation = coordinate
    clearFeaturesHighlighted()
    setCursor('grabbing')
  }

  /**
   * Begin split segment operation
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _startSplitSegment(event) {
    this._state.appendVertex = false
    this._handleAddVertex(event)
  }

  /**
   * Ends dragging a vertex.
   * @private
   */
  _endDraggingVertex() {
    const {
      undoQueue,
      draggingVertexIndex,
      startDragLocation,
      dragMoveLocation,
    } = this._state
    const coordinates = this._getCoordinates()

    // If vertex is dragged over one of the adjacent vertices, remove it
    if (
      this._isSameAsAdjacentVertex(
        coordinates,
        draggingVertexIndex,
        dragMoveLocation
      )
    ) {
      this._removeVertex(draggingVertexIndex, true, startDragLocation)
    } else {
      // Otherwise, commit the drag operation
      undoQueue.push({
        type: 'drag_vertex',
        index: draggingVertexIndex,
        startDragLocation,
      })
    }
    this._updateMarkup()
    this._state.draggingVertexIndex = null
    setCursor('grab')
  }

  /**
   * Returns whether a vertex is currently being dragged.
   * @return {boolean} - True if a vertex is being dragged, otherwise false.
   * @private
   */
  _isDraggingVertex() {
    return this._state.draggingVertexIndex !== null
  }

  /**
   * Starts the drag timer so we can differentiate between a click and a drag.
   * @param {string} operation - Drag operation type (e.g., 'drag_vertex')
   * @param {any} [args] - Drag operation arguments
   * @private
   */
  _startDragTimer(operation, args) {
    this._clearDragTimer()
    this.map.dragPan.disable()
    this._state.pendingDragOperation = operation
    this._state.pendingDragOperationArgs = args
    this._state.dragTimer = setTimeout(() => {
      this._clearDragTimer()
      switch (operation) {
        case 'drag_vertex':
          this._startDragVertex(args.coordinate, args.vertexIndex)
          break
        case 'split_segment':
          this._startSplitSegment(args)
          break
      }
    }, DRAG_DELAY)
  }

  /**
   * Clears pending drag timer (if any)
   * @private
   */
  _clearDragTimer() {
    if (this._state.dragTimer !== null) {
      clearTimeout(this._state.dragTimer)
      this._state.dragTimer = null
      this._state.pendingDragOperation = null
      this._state.pendingDragOperationArgs = null
    }
  }

  /**
   * Checks if there is a pending drag operation, and whether we should short-circuit
   * the drag delay timer and initiate the drag operation if the current mouse location
   * is far enough away from original click location.
   * @param {Point} point - Current mouse location
   * @returns {boolean} True if drag operation is now active
   * @private
   */
  _checkPendingDragOperation(point) {
    if (!this._state.pendingDragOperation) {
      return false
    }

    const { x: x0, y: y0 } = this._state.mouseDownLocation
    const { x: x1, y: y1 } = point
    const manhattanDist = Math.abs(x0 - x1) + Math.abs(y0 - y1)
    if (manhattanDist < DRAG_DIST_THRESHOLD) {
      return false
    }

    const operation = this._state.pendingDragOperation
    const args = this._state.pendingDragOperationArgs
    this._clearDragTimer()

    switch (operation) {
      case 'drag_vertex':
        this._startDragVertex(args.coordinate, args.vertexIndex)
        break
      case 'split_segment':
        this._startSplitSegment(args)
        break
    }

    return true
  }

  /**
   * Handles the vertex drag event.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleVertexDrag(event) {
    const eventLocation = [event.lngLat.lng, event.lngLat.lat]
    if (!isEqual(this._state.dragMoveLocation, eventLocation)) {
      this._state.dragMoveLocation = eventLocation
      this._state.dragMovePoint = event.point
      this.updated()
    }
    setCursor('grabbing')
  }

  /**
   * Handles the cursor hover point event.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleHoverPoint(event) {
    const coordinates = this._getCoordinates()
    const mouseLocation = [event.lngLat.lng, event.lngLat.lat]
    this._state.lineHoverLocation = getNearestPointOnLine(
      coordinates,
      mouseLocation
    )
    this.updated()
  }

  /**
   * Handles the adding vertex event.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleAddVertex(event) {
    // add new vertex at click location
    this._state.vertexAddLocation = [event.lngLat.lng, event.lngLat.lat]
    this._state.vertexAddId = uuid()
    this.updated()
  }

  /**
   * Returns vertex feature at given point location
   * @param {object} point - Point location
   * @param {string} [exceptId] - If passed in, ignore this vertex id
   * @returns {Feature|null} - Vertex feature at location, or null if none found
   * @private
   */
  _getVertexFeatureAtPoint(point, exceptId) {
    return this._getVertexFeaturesAtPoint(point, exceptId)[0] || null
  }

  /**
   * Returns an array of all vertex features at given point location
   * @param {object} point - Point location
   * @param {string} [exceptId] - If passed in, ignore this vertex id
   * @returns {Feature[]} - Array of vertex features at location (may be empty)
   * @private
   */
  _getVertexFeaturesAtPoint(point, exceptId) {
    const features = this.getFeaturesAtPoint(point)
    return features.filter(
      (feature) =>
        feature.properties.meta === 'vertex' &&
        (!exceptId || !exceptId.includes(feature.properties.id))
    )
  }

  /**
   * Whether a vertex should be added to the map features.  Helper for use in
   * rendering the line features.
   * @return {boolean} - True if a vertex should be added, otherwise false.
   * @private
   */
  _shouldAddVertex() {
    return (
      this._state.vertexAddLocation !== null && this._state.vertexAddId !== null
    )
  }

  /**
   * Adds a vertex coordinate to the given Line GeoJSON, generates a new
   * vertex ID, and adds a new distance label
   * @param {Object} lineGeoJson - GeoJSON for the line feature to add a vertex
   * coordinate to.
   * @private
   */
  _addVertex(lineGeoJson) {
    const newVertexIndex = this._getAddVertexIndex()
    const vertexCoordinate = this._state.vertexAddLocation
    const { coordinates } = lineGeoJson.geometry

    coordinates.splice(newVertexIndex, 0, vertexCoordinate)
    const vertexId = this._state.vertexAddId
    this._state.vertexIds.splice(newVertexIndex, 0, vertexId)
    this._state.undoQueue.push({
      type: 'add_vertex',
      index: newVertexIndex,
    })
    if (!this._state.appendVertex) {
      this._startDragVertex(vertexCoordinate, newVertexIndex)
    }
    this._state.lineHoverLocation = null
    this._state.vertexAddLocation = null
    this._state.vertexAddIndex = null
    this._state.vertexAddId = null

    const newLabels = []

    if (newVertexIndex > 0) {
      newLabels.push(
        this._generateLabel([
          coordinates[newVertexIndex - 1],
          coordinates[newVertexIndex],
        ])
      )
    }

    if (newVertexIndex < coordinates.length - 1) {
      newLabels.push(
        this._generateLabel([
          coordinates[newVertexIndex],
          coordinates[newVertexIndex + 1],
        ])
      )
    }

    this._state.labels.splice(newVertexIndex - 1, 1, ...newLabels)
    this._updateMarkup()
  }

  /**
   * Helper method to check if proposed vertex location is the same as either
   * of the adjacent vertices
   * @param {array} coords - Line coordinates array
   * @param {number} index - Proposed vertex index
   * @param {array} newVertex - Proposed vertex coordinates
   * @returns {boolean} True if proposed vertex location is same as one of the
   *  adjacent vertices
   * @private
   */
  _isSameAsAdjacentVertex(coords, index, newVertex) {
    const tolerance = getToleranceMeters(8)
    const prevVertex = index > 0 ? coords[index - 1] : null
    const nextVertex = index < coords.length - 1 ? coords[index + 1] : null
    const opts = { units: 'meters' }
    return (
      (prevVertex && distance(prevVertex, newVertex, opts) < tolerance) ||
      (nextVertex && distance(nextVertex, newVertex, opts) < tolerance)
    )
  }

  /**
   * Updates the dragged vertex coordinate in the given Line GeoJSON and
   * the corresponding distance labels
   * @param {Object} lineGeoJson - GeoJSON for the line feature being dragged
   * @private
   */
  _updateVertex(lineGeoJson) {
    const { draggingVertexIndex, dragMoveLocation, dragMovePoint, labels } =
      this._state
    const { coordinates } = lineGeoJson.geometry
    if (isEqual(coordinates[draggingVertexIndex], dragMoveLocation)) {
      return
    }

    // Check if there is an existing vertex at the given point
    const draggingVertexId = this._state.vertexIds[draggingVertexIndex]
    const existingVertex = this._getVertexFeatureAtPoint(
      dragMovePoint,
      draggingVertexId
    )
    if (existingVertex) {
      // Snap to existing vertex
      const existingVertexCoords = existingVertex.geometry.coordinates
      coordinates.splice(draggingVertexIndex, 1, existingVertexCoords)
      setFeaturesHighlighted([existingVertex])
    } else {
      coordinates.splice(draggingVertexIndex, 1, dragMoveLocation)
      clearFeaturesHighlighted()
    }

    if (draggingVertexIndex > 0) {
      const coords = [
        coordinates[draggingVertexIndex - 1],
        coordinates[draggingVertexIndex],
      ]
      const label = this._generateLabel(coords)
      labels.splice(draggingVertexIndex - 1, 1, label)
    }
    if (
      draggingVertexIndex >= 0 &&
      draggingVertexIndex !== coordinates.length - 1
    ) {
      const coords = [
        coordinates[draggingVertexIndex],
        coordinates[draggingVertexIndex + 1],
      ]
      const label = this._generateLabel(coords)
      labels.splice(draggingVertexIndex, 1, label)
    }
  }

  /**
   * Gets the index of the vertex to be added.
   * @return {number} The index position of the new vertex.
   * @private
   */
  _getAddVertexIndex() {
    if (this._state.vertexAddIndex !== null) {
      return this._state.vertexAddIndex
    }
    const vertexCoordinate = this._state.vertexAddLocation
    const coordinates = this._getCoordinates()
    return this._state.appendVertex
      ? coordinates.length
      : getIndexOfNewCoordinate(coordinates, vertexCoordinate)
  }

  /**
   * Removes the cursor hover point.
   * @private
   */
  _removeHoverPoint() {
    if (this._isHoveringLine()) {
      this._state.lineHoverLocation = null
      this.updated()
    }
  }

  /**
   * Whether the cursor is currently hovering the line.
   * @return {boolean} True if the cursor is hovering the line, false otherwise.
   * @private
   */
  _isHoveringLine() {
    return this._state.lineHoverLocation !== null
  }

  /**
   * Formats the line GeoJSON for rendering on the map.
   * @param {Object} lineGeoJson - Line feature GeoJSON.
   * @return {Object} Modified line feature GeoJSON.
   * @private
   */
  _formatLine(lineGeoJson) {
    if (!this._getFeature()) {
      return lineGeoJson
    }

    if (this._isDraggingVertex()) {
      this._updateVertex(lineGeoJson)
    }

    if (this._shouldAddVertex()) {
      this._addVertex(lineGeoJson)
    }

    return lineGeoJson
  }

  /** Formats a list of vertices for rendering on the map.
   * @param {Object} lineGeoJson - The line feature GeoJSON.
   * @return {Array<Object>} List of vertex GeoJSON features to render on the
   * map.
   * @private
   */
  _formatVertices(lineGeoJson) {
    const vertexIds = this._state.vertexIds
    const coordinatesList = lineGeoJson.geometry.coordinates
    const vertices = []

    // for long lines, only show the start and end vertices
    if (
      coordinatesList.length > VERTEX_LABEL_LENGTH_CUTOFF &&
      vertexIds.length > 1
    ) {
      //start vertex
      vertices.push(
        this._createVertexFeature(
          coordinatesList[0],
          vertexIds[0],
          lineGeoJson.properties.color
        )
      )
      //end vertex
      vertices.push(
        this._createVertexFeature(
          coordinatesList[coordinatesList.length - 1],
          vertexIds[vertexIds.length - 1],
          lineGeoJson.properties.color
        )
      )
    } else {
      for (let i = 0; i < vertexIds.length; i++) {
        vertices.push(
          this._createVertexFeature(
            coordinatesList[i],
            vertexIds[i],
            lineGeoJson.properties.color
          )
        )
      }
    }

    return vertices
  }

  _createVertexFeature(coordinates, id, color) {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id,
        meta: 'vertex',
        color: color,
      },
    }
  }

  /**
   * Formats a point GeoJSON feature for the cursor hover point.
   * @return {Object} Hover point GeoJSON feature to render on the map.
   * @private
   */
  _formatHoverPoint() {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: this._state.lineHoverLocation,
      },
      properties: {
        meta: 'hover',
      },
    }
  }

  /**
   * Returns the line feature
   * @return {object} Line feature
   */
  _getFeature() {
    const lineId = this._state.lineId
    return this.getFeature(lineId)
  }

  /**
   * Gets the line coordinates
   * @return {array} coordinates
   */
  _getCoordinates() {
    const lineFeature = this._getFeature()
    return lineFeature.geometry.coordinates
  }

  /**
   * Notify listeners when markup is updated
   */
  _updateMarkup() {
    const { lineId } = this._state
    const lineFeature = this.getFeature(lineId)
    const coordinates = lineFeature.geometry.coordinates
    this.events.emit(MARKUP_COORDINATES_CHANGED, { id: lineId, coordinates })
  }

  /**
   * Handles the undo button clicked.
   */
  _handleUndo() {
    const command = this._state.undoQueue.pop()
    if (!command) {
      return
    }
    switch (command.type) {
      case 'add_vertex':
        this._undoAddVertex(command)
        break
      case 'remove_vertex':
        this._undoRemoveVertex(command)
        break
      case 'drag_vertex':
        this._undoDragVertex(command)
        break
      case 'free_draw':
        this.setMode(FreehandDrawLineMode)
    }
  }

  /**
   * Undo add vertex command
   * @param {object} command - Add vertex command
   */
  _undoAddVertex(command) {
    this._removeVertex(command.index, false)
  }

  /**
   * Undo remove vertex command
   * @param {object} command - Remove vertex command
   */
  _undoRemoveVertex(command) {
    this._state.vertexAddIndex = command.index
    this._state.vertexAddLocation = command.coordinates
    this._state.vertexAddId = command.id
    this._updateMarkup()
    this.updated()
  }

  /**
   * Removes vertex at given index
   * @param {number} index - Index of vertex to remove
   * @param {boolean} allowUndo - If true, allow user to undo operation
   * @param {array} [originalCoords] - If passed in, use these coordinates
   *  for the undo history instead of the current vertex coordinates (allows
   *  proper undo of vertex removal when dragging a vertex over an adjacent
   *  vertex)
   * @private
   */
  _removeVertex(index, allowUndo, originalCoords) {
    const coordinates = this._getCoordinates()
    const id = this._state.vertexIds[index]
    const vertexCoords = coordinates[index]
    coordinates.splice(index, 1)
    this._state.labels.splice(index - 1, 1)
    this._state.vertexIds.splice(index, 1)
    if (allowUndo) {
      this._state.undoQueue.push({
        type: 'remove_vertex',
        index,
        id,
        coordinates: originalCoords || vertexCoords,
      })
    }
    this._updateMarkup()
    this.updated()
  }

  /**
   * Undo drag vertex command
   * @param {object} command - Drag vertex command
   */
  _undoDragVertex(command) {
    const coordinates = this._getCoordinates()
    coordinates.splice(command.index, 1, command.startDragLocation)
    this._state.labels = this._generateLabels({ geometry: { coordinates } })
    this._updateMarkup()
    this.updated()
  }
}

export default LineEditMode
